import { tr, usePartnerTranslation } from "../../lib/language";
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus,
  Check,
  Minus,
  MoreHorizontal,
  Pencil,
  Trash2,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "../../components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";
import { cn } from "../../lib/utils";
import { useAuth } from "../../lib/auth";
import AddUserModal, {
  ROLES,
  NOTIFICATION_CHANNELS,
} from "../../components/portal/account/AddUserModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLE_BY_VALUE = Object.fromEntries(ROLES.map((r) => [r.value, r]));

const RolePill = ({ user }) => {
  usePartnerTranslation();
  if (!user.allow_login) {
    return (
      <span
        data-testid={`row-role-${user.id}`}
        className="inline-flex items-center rounded-full bg-[#FAFAFB] px-2 py-0.5 text-[11px] font-semibold text-[#6B7280]"
      >{tr("No login")}</span>
    );
  }
  const role = ROLE_BY_VALUE[user.role] || { label: user.role || "Member" };
  return (
    <span
      data-testid={`row-role-${user.id}`}
      className="inline-flex items-center rounded-full bg-[#FFEFEF] px-2 py-0.5 text-[11px] font-semibold text-[#9B0000]"
    >
      {role.label}
    </span>
  );
};

const ChannelDot = ({
  on,
  testid
}) => {
  usePartnerTranslation();
  return <span data-testid={testid} aria-label={on ? tr("yes") : tr("no")} className={cn("inline-flex h-5 w-5 items-center justify-center rounded-full", on ? "bg-[#0F6B2B]/10 text-[#0F6B2B]" : "bg-[#F3EEEE] text-[#9BA1AB]")}>
    {on ? <Check className="h-3 w-3" strokeWidth={2.4} /> : <Minus className="h-3 w-3" />}
  </span>;
};

const RowActions = ({ user, viewerIsOwner, onEdit, onDelete }) => {
  usePartnerTranslation();
  const canEdit = !user.is_owner || viewerIsOwner;
  const canDelete = !user.is_owner;
  if (!canEdit && !canDelete) return null;
  return (
  <DropdownMenu>
    <DropdownMenuTrigger
      data-testid={`row-actions-${user.id}`}
      className="inline-flex h-8 w-8 items-center justify-center rounded-md text-[#6B7280] hover:bg-[#F3EEEE] hover:text-[#0F1115]"
    >
      <MoreHorizontal className="h-4 w-4" />
    </DropdownMenuTrigger>
    <DropdownMenuContent
      align="end"
      className="w-44 rounded-md border border-[#EAECEF] p-1 shadow-lg"
    >
      {canEdit && (
      <DropdownMenuItem
        onClick={() => onEdit(user)}
        data-testid={`row-edit-${user.id}`}
        className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm"
      >
        <Pencil className="h-3.5 w-3.5 text-[#6B7280]" />{tr("Edit")}</DropdownMenuItem>
      )}
      {canDelete && (
        <DropdownMenuItem
          onClick={() => onDelete(user)}
          data-testid={`row-delete-${user.id}`}
          className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm text-[#9B0000]"
        >
          <Trash2 className="h-3.5 w-3.5" />{tr("Remove")}</DropdownMenuItem>
      )}
    </DropdownMenuContent>
  </DropdownMenu>
  );
};

const DeleteUserModal = ({
  user,
  onCancel,
  onConfirm,
  busy
}) => {
  usePartnerTranslation();
  return <Dialog open={!!user} onOpenChange={v => !v && !busy && onCancel()}>
    <DialogContent data-testid="delete-user-modal" className="max-w-[420px] gap-0 rounded-2xl border-[#EFE4E4] p-0">
      <DialogHeader className="border-b border-[#EFE4E4] px-6 py-4">
        <DialogTitle className="text-lg font-extrabold tracking-tight text-[#0F1115]">{tr("Remove this user?")}</DialogTitle>
        <DialogDescription className="text-sm text-[#6B7280]">
          {user?.email}{tr(" will lose access immediately. You can re-invite them anytime.")}</DialogDescription>
      </DialogHeader>
      <div className="flex flex-col-reverse items-stretch justify-end gap-2 px-6 py-4 sm:flex-row sm:items-center">
        <button type="button" onClick={onCancel} disabled={busy} data-testid="delete-user-cancel" className="inline-flex h-10 items-center justify-center rounded-md border border-[#E6E8EC] px-4 text-sm font-semibold text-[#0F1115] transition-colors hover:border-[#0F1115] disabled:opacity-60">{tr("Keep")}</button>
        <button type="button" onClick={onConfirm} disabled={busy} data-testid="delete-user-confirm" className="inline-flex h-10 items-center justify-center rounded-md bg-[#9B0000] px-5 text-sm font-semibold text-white transition-colors hover:bg-[#7a0000] disabled:opacity-60">
          {busy ? tr("Removing…") : tr("Remove user")}
        </button>
      </div>
    </DialogContent>
  </Dialog>;
};

/* ---------- Mobile card ---------- */
const UserCard = ({ user, viewerIsOwner, onEdit, onDelete }) => {
  usePartnerTranslation();
  const channels = new Set(user.notification_prefs || []);
  return (
    <div
      data-testid={`user-card-${user.id}`}
      className="rounded-xl border border-[#F0F2F5] bg-white p-4"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-[#0F1115]">
            {user.email}
          </p>
          {user.full_name && (
            <p className="mt-0.5 truncate text-xs text-[#6B7280]">
              {user.full_name}
              {user.job_title ? ` · ${user.job_title}` : ""}
            </p>
          )}
        </div>
        <RowActions user={user} viewerIsOwner={viewerIsOwner} onEdit={onEdit} onDelete={onDelete} />
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        <RolePill user={user} />
        {user.is_owner && (
          <span className="inline-flex items-center rounded-full bg-[#FFEFEF] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[#FF5757]">{tr("Owner")}</span>
        )}
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2">
        {NOTIFICATION_CHANNELS.map((c) => {
          const on = user.receive_notifications && channels.has(c.value);
          return (
            <div
              key={c.value}
              className="flex flex-col items-center gap-1 rounded-md bg-[#FAFAFB] px-1.5 py-2"
            >
              <ChannelDot
                on={on}
                testid={`row-${user.id}-channel-${c.value}`}
              />
              <span className="text-center text-[10px] font-semibold text-[#6B7280]">
                {c.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default function AccountManagementPage() {
  usePartnerTranslation();
  const { user: currentUser } = useAuth();
  const viewerIsOwner = !!currentUser?.is_owner;
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const refresh = async () => {
    try {
      const r = await axios.get(`${API}/account/users`);
      setUsers(r.data?.items || []);
    } catch {
      toast.error(tr("Couldn't load users"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const handleEdit = (user) => {
    setEditingUser(user);
    setShowAdd(true);
  };

  const closeModal = () => {
    setShowAdd(false);
    setEditingUser(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/account/users/${deleteTarget.id}`);
      toast.success(tr("User removed"));
      setDeleteTarget(null);
      refresh();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(tr("Couldn't remove user"), {
        description: typeof detail === "string" ? detail : undefined,
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <main
      data-testid="account-management-page"
      className="mx-auto w-full max-w-[1200px] flex-1 px-4 py-6 sm:px-6 sm:py-8 md:px-10"
    >
      <header className="mb-6 flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-end">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-[#6B7280]">{tr("Settings")}</p>
          <h1 className="text-2xl font-extrabold tracking-tight text-[#0F1115] sm:text-3xl">{tr("Account management")}</h1>
          <p className="mt-1 text-sm text-[#6B7280]">{tr("Manage who can sign in and what they can see.")}</p>
        </div>
        <button
          type="button"
          onClick={() => setShowAdd(true)}
          data-testid="add-user-btn"
          className="humrahe-btn-primary inline-flex h-10 w-full items-center justify-center gap-1.5 rounded-md px-4 text-sm font-semibold sm:w-auto"
        >
          <Plus className="h-4 w-4" />{tr("Add a new user profile")}</button>
      </header>

      {/* Mobile: cards */}
      <div
        data-testid="users-mobile-list"
        className="space-y-2.5 md:hidden"
      >
        {loading && (
          <p className="rounded-xl border border-[#F0F2F5] bg-white px-4 py-10 text-center text-sm text-[#9BA1AB]">{tr("Loading…")}</p>
        )}
        {!loading && users.length === 0 && (
          <p className="rounded-xl border border-[#F0F2F5] bg-white px-4 py-10 text-center text-sm text-[#9BA1AB]">{tr("No users yet. Add a teammate to get started.")}</p>
        )}
        {!loading &&
          users.map((u) => (
            <UserCard
              key={u.id}
              user={u}
              viewerIsOwner={viewerIsOwner}
              onEdit={handleEdit}
              onDelete={setDeleteTarget}
            />
          ))}
      </div>

      {/* Desktop: table */}
      <div
        data-testid="users-table-wrap"
        className="hidden overflow-hidden rounded-xl border border-[#F0F2F5] bg-white md:block"
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="users-table">
            <thead className="border-b border-[#F0F2F5] bg-[#FAFAFB] text-start text-[11px] uppercase tracking-wider text-[#6B7280]">
              <tr>
                <th className="px-4 py-3 font-semibold">{tr("User")}</th>
                <th className="px-4 py-3 font-semibold">{tr("Role")}</th>
                {NOTIFICATION_CHANNELS.map((c) => (
                  <th
                    key={c.value}
                    className="px-2 py-3 text-center font-semibold"
                  >
                    {c.label}
                  </th>
                ))}
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td
                    colSpan={3 + NOTIFICATION_CHANNELS.length}
                    className="px-4 py-10 text-center text-sm text-[#9BA1AB]"
                  >{tr("Loading…")}</td>
                </tr>
              )}
              {!loading && users.length === 0 && (
                <tr>
                  <td
                    colSpan={3 + NOTIFICATION_CHANNELS.length}
                    className="px-4 py-10 text-center text-sm text-[#9BA1AB]"
                  >{tr("No users yet. Add a teammate to get started.")}</td>
                </tr>
              )}
              {!loading &&
                users.map((u) => {
                  const channels = new Set(u.notification_prefs || []);
                  return (
                    <tr
                      key={u.id}
                      data-testid={`user-row-${u.id}`}
                      className="border-t border-[#F0F2F5] hover:bg-[#FAFAFB]"
                    >
                      <td className="px-4 py-3">
                        <p className="font-semibold text-[#0F1115]">
                          {u.full_name || u.email}
                        </p>
                        <p className="text-xs text-[#6B7280]">
                          {u.full_name ? u.email : u.job_title || "—"}
                          {u.is_owner && (
                            <span className="ms-2 inline-flex items-center rounded-full bg-[#FFEFEF] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[#FF5757]">{tr("Owner")}</span>
                          )}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <RolePill user={u} />
                      </td>
                      {NOTIFICATION_CHANNELS.map((c) => (
                        <td key={c.value} className="px-2 py-3 text-center">
                          <ChannelDot
                            on={u.receive_notifications && channels.has(c.value)}
                            testid={`row-${u.id}-channel-${c.value}`}
                          />
                        </td>
                      ))}
                      <td className="px-4 py-3 text-end">
                        <RowActions
                          user={u}
                          viewerIsOwner={viewerIsOwner}
                          onEdit={handleEdit}
                          onDelete={setDeleteTarget}
                        />
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>

      <AddUserModal
        open={showAdd}
        editingUser={editingUser}
        onClose={closeModal}
        onCreated={refresh}
      />
      <DeleteUserModal
        user={deleteTarget}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        busy={deleting}
      />
    </main>
  );
}
